import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { authService } from './authService';
import React from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10);
const ENABLE_CONSOLE_LOGS = import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true';
const HOOPERS_API_KEY = import.meta.env.VITE_HOOPERS_API_KEY || '';

// Global callback to update user profile context
let userProfileUpdateCallback: ((data: any) => void) | null = null;

// Export function to register callback from UserProfileContext
export const registerUserProfileUpdateCallback = (callback: (data: any) => void) => {
  userProfileUpdateCallback = callback;
};

export const unregisterUserProfileUpdateCallback = () => {
  userProfileUpdateCallback = null;
};

class HttpClient {
  private instance: AxiosInstance;
  private authToken: string | null = null;
  private pendingRequests = new Map<string, AbortController>();
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(HOOPERS_API_KEY && { 'hoopers-api-key': HOOPERS_API_KEY }),
      },
    });

    // Only load token from localStorage if we don't already have one
    // This prevents hot-reload from overwriting a newly set token
    if (!this.authToken) {
      const token = localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
      if (token) {
        this.authToken = token;
      }
    }

    this.setupInterceptors();
  }

  private logRequest(config: AxiosRequestConfig) {
    // Logging disabled - use ENABLE_CONSOLE_LOGS env var if needed
  }

  private logResponse(response: AxiosResponse) {
    // Logging disabled - use ENABLE_CONSOLE_LOGS env var if needed
  }

  private logError(error: AxiosError) {
    if (ENABLE_CONSOLE_LOGS) {
      if (error.response) {
        console.error(
          `[API Error] ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          {
            data: error.response.data,
            status: error.response.status,
            headers: error.response.headers,
          }
        );
      } else if (error.request) {
        console.error('[API Error] No response received', error.request);
      } else {
        console.error('[API Error]', error.message);
      }
    }
  }

  private getRequestKey(config: AxiosRequestConfig) {
    return `${config.method}-${config.url}-${JSON.stringify(config.params)}-${JSON.stringify(config.data)}`;
  }

  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      config => {
        this.logRequest(config);

        // Add hoopers-api-key header
        if (HOOPERS_API_KEY) {
          config.headers['hoopers-api-key'] = HOOPERS_API_KEY;
        }

        // Add auth token - ALWAYS check localStorage first to get the most recent token
        const token = localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
        if (token) {
          this.authToken = token;
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Cancel duplicate requests (except for /api/users/me, comparator and datasources endpoints)
        const key = this.getRequestKey(config);
        const shouldNotCancel = config.url?.includes('/api/users/me') ||
                                config.url?.includes('/api/v2/comparator') ||
                                config.url?.includes('/api/v2/datasources');
        if (this.pendingRequests.has(key) && !shouldNotCancel) {
          this.pendingRequests.get(key)?.abort();
          this.pendingRequests.delete(key);
        }
        const controller = new AbortController();
        config.signal = controller.signal;
        this.pendingRequests.set(key, controller);

        return config;
      },
      error => {
        this.logError(error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      response => {
        this.logResponse(response);
        const key = this.getRequestKey(response.config);
        this.pendingRequests.delete(key);
        
        // Auto-update UserProfileContext when /api/users/me is called
        // Update on GET (fetch profile) and PATCH (update profile) to ensure metrics are refreshed
        if (response.config.url && response.config.url.includes('/api/users/me') && response.data) {
          // Update on GET (profile fetch) and PATCH (profile update) requests
          const method = response.config.method?.toLowerCase();
          if (method === 'get' || method === 'patch') {
            try {
              if (userProfileUpdateCallback) {
                userProfileUpdateCallback(response.data);
              }
            } catch (error) {
              console.error('[HttpClient] Error updating user profile context:', error);
            }
          }
        }
        
        return response;
      },
      async (error: unknown) => {
        // Handle canceled requests
        if (axios.isCancel(error)) {
          return Promise.resolve({ data: {} });
        }

        // Handle Axios errors
        if (axios.isAxiosError(error)) {
          this.logError(error);

          // Clean up pending requests
          if (error.config) {
            const key = this.getRequestKey(error.config);
            this.pendingRequests.delete(key);
          }

          // Handle 401 Unauthorized - attempt token refresh before redirecting
          if (error.response?.status === 401 && error.config) {
            const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

            // Skip refresh for auth endpoints (login, refresh itself) or already-retried requests
            const isAuthEndpoint = originalRequest.url?.includes('/api/login') ||
                                   originalRequest.url?.includes('/api/auth/refresh') ||
                                   originalRequest.url?.includes('/api/register');

            if (isAuthEndpoint || originalRequest._retry) {
              // Refresh failed or auth endpoint error — redirect to login
              if (this.isTokenExpired()) {
                this.redirectToLogin();
              }
              return Promise.reject(error);
            }

            originalRequest._retry = true;

            // If a refresh is already in progress, queue this request
            if (this.isRefreshing) {
              return new Promise<AxiosResponse>((resolve) => {
                this.refreshSubscribers.push((newToken: string) => {
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  resolve(this.instance(originalRequest));
                });
              });
            }

            this.isRefreshing = true;

            try {
              const newToken = await authService.refreshToken();
              // Notify all queued requests with the new token
              this.refreshSubscribers.forEach(cb => cb(newToken));
              this.refreshSubscribers = [];
              // Retry the original request
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.instance(originalRequest);
            } catch {
              this.refreshSubscribers = [];
              this.redirectToLogin();
              return Promise.reject(error);
            } finally {
              this.isRefreshing = false;
            }
          }

          return Promise.reject(error);
        }

        // Handle non-Axios errors
        console.error('Non-Axios error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Check if JWT token is expired
   * @returns true if token is expired or invalid, false otherwise
   */
  private isTokenExpired(): boolean {
    const token = localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
    if (!token) return true;

    try {
      // Decode JWT payload (base64 encoded middle part)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expirationTime;
    } catch (e) {
      // If we can't decode the token, consider it expired
      return true;
    }
  }

  private redirectToLogin() {
    this.clearAuth();
    localStorage.removeItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  public setAuthToken(token: string | null) {
    this.authToken = token;
    if (token) {
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.instance.defaults.headers.common['Authorization'];
    }
  }

  public clearAuth() {
    this.authToken = null;
    delete this.instance.defaults.headers.common['Authorization'];

    // Cancel all pending requests on logout
    this.pendingRequests.forEach(controller => controller.abort());
    this.pendingRequests.clear();
  }

  /**
   * Validates that an endpoint starts with /api/ in development and test environments
   * @param url - The endpoint URL to validate
   * @throws Error in test mode if endpoint doesn't start with /api/
   */
  private validateEndpoint(url: string): void {
    // Only validate in development and test environments
    if (import.meta.env.MODE === 'test' || import.meta.env.DEV) {
      if (typeof url === 'string' && !url.startsWith('/api/')) {
        const error = new Error(
          `❌ Invalid API endpoint: "${url}"\n` +
          `All endpoints must start with "/api/"\n` +
          `Expected: "/api/${url.replace(/^\/+/, '')}"`
        );

        // In test mode, throw error to fail the test
        if (import.meta.env.MODE === 'test') {
          throw error;
        }

        // In dev mode, log error but don't block
        console.error(error.message);
      }
    }
  }

  // HTTP Methods
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    try {
      const response = await this.instance.get<T>(url, { ...config, signal: config?.signal });
      return response.data;
    } catch (error) {
      this.handleError(error, url);
      throw error;
    }
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  private handleError(error: unknown, url: string) {
    // Ignorar erros de requisições canceladas
    if (axios.isCancel(error)) {
      return;
    }

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        // Request timeout
      } else if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Request error:', error.message);
      }
    } else if (error instanceof Error) {
      console.error('Error:', error.message);
    } else {
      console.error('Unknown error occurred');
    }
  }
}

// Singleton
export const httpClient = new HttpClient();

// Hook para React components
export const useHttpClient = () => {
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const authToken = authService.getAccessToken();
    if (authToken) {
      httpClient.setAuthToken(authToken);
    }
  }, []);

  return httpClient;
};

export default httpClient;
